const LS_HISTORY = "smartRoute:addressHistory_v1";
const LS_LAST_TRIP = "smartRoute:lastTrip_v1";
/** Cap recent From/To picks — reuse coords to avoid extra Google geocode/Places calls. */
export const MAX_RECENT_TRAVEL_ADDRESSES = 10;
const MAX_HISTORY = MAX_RECENT_TRAVEL_ADDRESSES;

export const normalizeTravelAddressKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** True when two Drive From/To endpoints refer to the same place. */
export function travelAddressesMatch(a, b) {
  if (!a || !b) return false;

  const placeIdA = String(a.placeId || "").trim();
  const placeIdB = String(b.placeId || "").trim();
  if (placeIdA && placeIdB && placeIdA === placeIdB) return true;

  const latA = Number(a.lat);
  const lngA = Number(a.lng);
  const latB = Number(b.lat);
  const lngB = Number(b.lng);
  if (
    Number.isFinite(latA) &&
    Number.isFinite(lngA) &&
    Number.isFinite(latB) &&
    Number.isFinite(lngB)
  ) {
    return Math.abs(latA - latB) < 1e-3 && Math.abs(lngA - lngB) < 1e-3;
  }

  const addrA = normalizeTravelAddressKey(a.formattedAddress || a.address || "");
  const addrB = normalizeTravelAddressKey(b.formattedAddress || b.address || "");
  return Boolean(addrA && addrB && addrA === addrB);
}

export function loadTravelAddressHistory() {
  try {
    const raw = localStorage.getItem(LS_HISTORY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

/**
 * Last N recently used Drive addresses (with lat/lng), newest first.
 * Prefer these over Places autocomplete to cut Google Maps cost.
 */
export function getRecentTravelAddresses(history = loadTravelAddressHistory(), limit = MAX_HISTORY) {
  const out = [];
  const seen = new Set();
  for (const h of history || []) {
    const lat = Number(h.lat);
    const lng = Number(h.lng);
    const address = String(h.formattedAddress || h.address || "").trim();
    if (!address || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const key = `${h.placeId || ""}|${normalizeTravelAddressKey(address)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: `recent-${key}`,
      kind: "recent",
      label: null,
      address,
      lat,
      lng,
      placeId: h.placeId || null,
      usedAt: h.usedAt || null,
    });
    if (out.length >= limit) break;
  }
  return out;
}

function persistHistory(list) {
  try {
    localStorage.setItem(LS_HISTORY, JSON.stringify(list.slice(0, MAX_HISTORY)));
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ address?: string, formattedAddress?: string, lat?: number, lng?: number, placeId?: string|null }} entry
 */
export function rememberTravelAddress(entry) {
  const lat = Number(entry?.lat);
  const lng = Number(entry?.lng);
  const formattedAddress = String(entry?.formattedAddress || entry?.address || "").trim();
  if (!formattedAddress || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

  const row = {
    address: formattedAddress,
    formattedAddress,
    lat,
    lng,
    placeId: entry?.placeId || null,
    usedAt: Date.now(),
  };

  const key = `${row.placeId || ""}|${normalizeTravelAddressKey(formattedAddress)}`;
  const list = loadTravelAddressHistory().filter(
    (x) => `${x.placeId || ""}|${normalizeTravelAddressKey(x.formattedAddress || x.address)}` !== key,
  );
  persistHistory([row, ...list]);
}

export function looseTravelAddressKey(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\bfrance\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function findTravelAddressInHistory(list, addressText, placeId = null) {
  const pid = String(placeId || "").trim();
  if (pid) {
    const byPid = (list || []).find((x) => x.placeId === pid);
    if (byPid) return byPid;
  }
  const norm = normalizeTravelAddressKey(addressText);
  if (!norm) return null;
  const exact = (list || []).find(
    (x) => normalizeTravelAddressKey(x.formattedAddress || x.address) === norm,
  );
  if (exact) return exact;
  const want = looseTravelAddressKey(addressText);
  if (!want || want.length < 12 || !/\d/.test(want)) return null;
  return (
    (list || []).find((row) => {
      const key = looseTravelAddressKey(row?.formattedAddress || row?.address);
      if (!key) return false;
      if (key === want) return true;
      const shorter = key.length <= want.length ? key : want;
      const longer = shorter === key ? want : key;
      return /\d/.test(shorter) && shorter.length >= 12 && longer.includes(shorter);
    }) || null
  );
}

export function loadLastTravelTrip() {
  try {
    const raw = localStorage.getItem(LS_LAST_TRIP);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveLastTravelTrip(trip) {
  try {
    if (!trip?.origin?.formattedAddress && !trip?.destination?.formattedAddress) return;
    localStorage.setItem(LS_LAST_TRIP, JSON.stringify({ ...trip, savedAt: Date.now() }));
  } catch {
    /* ignore */
  }
}

export function clearLastTravelTrip() {
  try {
    localStorage.removeItem(LS_LAST_TRIP);
  } catch {
    /* ignore */
  }
}

const LS_CLEAR_ENDPOINTS = "smartRoute:clearEndpointsAfterOrder_v1";

/** After a successful Drive order — clear trip storage and skip autofill on next Explore. */
export function markDriveEndpointsClearedAfterOrder() {
  try {
    sessionStorage.setItem(LS_CLEAR_ENDPOINTS, "1");
  } catch {
    /* ignore */
  }
  clearLastTravelTrip();
}

export function consumeDriveEndpointsClearedFlag() {
  try {
    const raw = sessionStorage.getItem(LS_CLEAR_ENDPOINTS);
    if (!raw) return false;
    sessionStorage.removeItem(LS_CLEAR_ENDPOINTS);
    return true;
  } catch {
    return false;
  }
}

/** Drive destination presets (Get me Home / Get me Office). */
export const DRIVE_DEST_HOME = "Home";
export const DRIVE_DEST_OFFICE = "Office";
/** Drive corridor presets — From+To pair (reverse shares the same addresses). */
export const DRIVE_ROUTE_HOME_TO_OFFICE = "Home to Office";
export const DRIVE_ROUTE_OFFICE_TO_HOME = "Office to Home";

export const DRIVE_DEST_LABELS = [DRIVE_DEST_HOME, DRIVE_DEST_OFFICE];
export const DRIVE_ROUTE_LABELS = [DRIVE_ROUTE_HOME_TO_OFFICE, DRIVE_ROUTE_OFFICE_TO_HOME];

const normalizeDriveLabelKey = (label) =>
  String(label || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Map legacy Drive "Work" → Office for lookups. */
export function normalizeDrivePlaceLabel(label) {
  const key = normalizeDriveLabelKey(label);
  if (key === "work") return DRIVE_DEST_OFFICE;
  if (key === "home") return DRIVE_DEST_HOME;
  if (key === "home to office" || key === "home → office" || key === "home->office") {
    return DRIVE_ROUTE_HOME_TO_OFFICE;
  }
  if (key === "office to home" || key === "office → home" || key === "office->home") {
    return DRIVE_ROUTE_OFFICE_TO_HOME;
  }
  return String(label || "").trim();
}

export function isDriveRouteLabel(label) {
  const n = normalizeDrivePlaceLabel(label);
  return n === DRIVE_ROUTE_HOME_TO_OFFICE || n === DRIVE_ROUTE_OFFICE_TO_HOME;
}

export function isDriveDestinationLabel(label) {
  const n = normalizeDrivePlaceLabel(label);
  return n === DRIVE_DEST_HOME || n === DRIVE_DEST_OFFICE;
}

export function findDrivePlaceByLabel(places, label) {
  const want = normalizeDriveLabelKey(normalizeDrivePlaceLabel(label));
  return (
    (places || []).find((p) => {
      const got = normalizeDriveLabelKey(normalizeDrivePlaceLabel(p?.label));
      return got && got === want;
    }) || null
  );
}

/** Destination places only (excludes Drive corridor / route presets). */
export function isDestinationPlace(place) {
  if (!place) return false;
  if (place.kind === "route") return false;
  return !isDriveRouteLabel(place.label);
}

/**
 * Shared Home/Work/Office/… destinations for Near Me + Drive.
 * Work and Office are treated as the same label; prefers nearMe when both exist.
 */
export function sharedDestinationPlaces(places) {
  const byKey = new Map();
  for (const p of places || []) {
    if (!isDestinationPlace(p)) continue;
    const key = normalizeDriveLabelKey(normalizeDrivePlaceLabel(p.label) || p.label);
    if (!key) continue;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, p);
      continue;
    }
    const existingIsDrive = existing.scope === "drive";
    const nextIsDrive = p.scope === "drive";
    if (existingIsDrive && !nextIsDrive) {
      byKey.set(key, p);
    }
  }
  return [...byKey.values()];
}

/** Near Me chips use "Work"; Drive may store "Office" — normalize for Near Me UI. */
export function placesForNearMeDisplay(places) {
  const PRIORITY = { home: 0, work: 1, office: 1 };
  return sharedDestinationPlaces(places)
    .map((p) => {
      const n = normalizeDrivePlaceLabel(p.label);
      if (n === DRIVE_DEST_OFFICE) return { ...p, label: "Work" };
      return p;
    })
    .sort((a, b) => {
      const aKey = normalizeDriveLabelKey(a.label);
      const bKey = normalizeDriveLabelKey(b.label);
      const aRank = Object.prototype.hasOwnProperty.call(PRIORITY, aKey)
        ? PRIORITY[aKey]
        : 50;
      const bRank = Object.prototype.hasOwnProperty.call(PRIORITY, bKey)
        ? PRIORITY[bKey]
        : 50;
      if (aRank !== bRank) return aRank - bRank;
      return String(a.label || "").localeCompare(String(b.label || ""));
    });
}

/** Drive chips + suggestions: shared destinations plus any legacy saved routes. */
export function placesForDriveDisplay(places) {
  const destinations = sharedDestinationPlaces(places);
  const routes = (places || []).filter(
    (p) => p?.kind === "route" || isDriveRouteLabel(p?.label),
  );
  const seen = new Set(destinations.map((p) => String(p._id)));
  const extraRoutes = routes.filter((p) => !seen.has(String(p._id)));
  return [...destinations, ...extraRoutes];
}

export function reverseDriveRouteLabel(label) {
  const n = normalizeDrivePlaceLabel(label);
  if (n === DRIVE_ROUTE_HOME_TO_OFFICE) return DRIVE_ROUTE_OFFICE_TO_HOME;
  if (n === DRIVE_ROUTE_OFFICE_TO_HOME) return DRIVE_ROUTE_HOME_TO_OFFICE;
  return null;
}

/**
 * Merge saved destinations (shared across Near Me + Drive) + recent history for quick pick.
 */
export function buildTravelAddressSuggestions({ places = [], history = [] }) {
  const seen = new Set();
  const out = [];

  const push = (row) => {
    const addr = String(row.address || "").trim();
    if (!addr) return;
    const key = `${row.placeId || ""}|${normalizeTravelAddressKey(addr)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  };

  // Recent first — From/To focus lists prefer these (cached coords, no Places call).
  for (const row of getRecentTravelAddresses(history, MAX_HISTORY)) {
    push(row);
  }

  for (const p of places || []) {
    const coords = p?.coords;
    const lat = Number(coords?.lat);
    const lng = Number(coords?.lng);
    const placeKind = p?.kind === "route" ? "route" : "destination";
    const id = String(p._id || p.id || "");
    const label = normalizeDrivePlaceLabel(p.label) || p.label || null;

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      push({
        id,
        kind: "saved",
        placeKind,
        label,
        address: String(p.formattedAddress || p.address || "").trim(),
        lat,
        lng,
        placeId: p.placeId || null,
        toAddress: p.toAddress || null,
        toLat: Number(p.toCoords?.lat),
        toLng: Number(p.toCoords?.lng),
        toPlaceId: p.toPlaceId || null,
      });
    }

    // Also expose the To endpoint of saved routes as a pickable address.
    if (placeKind === "route") {
      const toLat = Number(p?.toCoords?.lat);
      const toLng = Number(p?.toCoords?.lng);
      const toAddr = String(p?.toAddress || "").trim();
      if (toAddr && Number.isFinite(toLat) && Number.isFinite(toLng)) {
        push({
          id: `${id}-to`,
          kind: "saved",
          placeKind: "route-endpoint",
          label: null,
          address: toAddr,
          lat: toLat,
          lng: toLng,
          placeId: p.toPlaceId || null,
        });
      }
    }
  }

  return out;
}
